import { UserProfileIcon } from '@components/avatars';
import { timeago } from 'shared/dateutil';
import { Member, Segment } from './types';

function segmentAccent(segment: Segment) {
  switch (segment) {
    case 'champions':
      return 'border-l-green-500';
    case 'fading':
      return 'border-l-amber-400';
    case 'inactive':
      return 'border-l-gray-300';
    case 'new':
      return 'border-l-blue-500';
  }
}

function memberSince(createdAt: string) {
  const days = Math.floor((Date.now() - new Date(createdAt).getTime()) / (1000 * 60 * 60 * 24));
  if (days < 30) {
    return `${days}d`;
  }
  const months = Math.floor(days / 30);
  if (months < 12) {
    return `${months}mo`;
  }
  const years = Math.floor(months / 12);
  return `${years}y`;
}

function MilestoneBadge({ done, label }: { done: boolean; label: string }) {
  return (
    <span
      class={`inline-flex items-center text-xs px-2 py-0.5 rounded-full ${
        done ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-400'
      }`}
    >
      {done ? '✓' : '—'} {label}
    </span>
  );
}

export function MemberCard({ member, segment }: { member: Member; segment: Segment }) {
  const accent = segmentAccent(segment);

  return (
    <div class={`border border-l-4 ${accent} rounded-lg p-4 flex items-start gap-4`}>
      <UserProfileIcon user={member} size="w-10 h-10" />
      <div class="flex-1 min-w-0">
        <div class="flex items-start justify-between gap-2">
          <div>
            <div class="font-medium text-gray-900">{member.name}</div>
            <div class="text-sm text-gray-500 truncate">{member.email}</div>
          </div>
          {segment === 'champions' && (
            <span class="text-sm font-medium text-gray-500 shrink-0">
              Score: {member.engagementScore}
            </span>
          )}
        </div>
        {segment === 'new' && (
          <div class="mt-2 flex flex-wrap gap-2">
            <MilestoneBadge done={!!member.hasCompleteProfile} label="Profile" />
            <MilestoneBadge done={!!member.hasPosted} label="First post" />
            <MilestoneBadge done={!!member.hasReplied} label="First reply" />
          </div>
        )}
        <div class="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
          <span>
            Active {member.activeDays} day{member.activeDays !== 1 ? 's' : ''}
          </span>
          <span>
            {member.postCount} post{member.postCount !== 1 ? 's' : ''}
          </span>
          <span>
            {member.replyCount} repl{member.replyCount !== 1 ? 'ies' : 'y'}
          </span>
          <span>
            {member.likesGiven} like{member.likesGiven !== 1 ? 's' : ''}
          </span>
        </div>
        <div class="mt-2 flex items-center gap-4 text-xs text-gray-400">
          {segment === 'new' && member.enrolledAt ? (
            <span>Joined {timeago(member.enrolledAt)}</span>
          ) : (
            <span>Active {member.lastActiveAt ? timeago(member.lastActiveAt) : 'never'}</span>
          )}
          <span>Member {memberSince(member.createdAt)}</span>
          <a href={`/admin/people/${member.id}`} class="text-indigo-600 hover:text-indigo-800">
            View
          </a>
          <a href={`mailto:${member.email}`} class="text-indigo-600 hover:text-indigo-800">
            Email
          </a>
        </div>
      </div>
    </div>
  );
}
